import React, { useEffect, useRef, useState } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { getCallScribeRecording } from 'services/callScribe';
import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';
import { formatClock } from 'components/CallScribe/EmotionTimeline/emotionSpectrumModel';

export interface ICallRecordingPlayerProps {
  callId: number;
  seekMs?: number | null;
  durationSeconds?: number | null;
}

const BARS = 56;

export const PEAKS_SAMPLE_RATE = 3000;
export const PEAKS_MAX_SECONDS = 20 * 60;
export const PEAKS_MAX_BYTES = 12 * 1024 * 1024;

type TOfflineAudioContextCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

export const computePeaks = async (
  blob: Blob,
  bars = BARS,
  durationSeconds?: number | null,
): Promise<number[] | null> => {
  if (typeof durationSeconds === 'number' && durationSeconds > PEAKS_MAX_SECONDS) return null;
  if (blob.size > PEAKS_MAX_BYTES) return null;
  const scope = window as unknown as {
    OfflineAudioContext?: TOfflineAudioContextCtor;
    webkitOfflineAudioContext?: TOfflineAudioContextCtor;
  };
  const Ctor = scope.OfflineAudioContext ?? scope.webkitOfflineAudioContext;
  if (!Ctor || typeof blob.arrayBuffer !== 'function') return null;
  try {
    const ctx = new Ctor(1, 1, PEAKS_SAMPLE_RATE);
    const audio = await ctx.decodeAudioData(await blob.arrayBuffer());
    const size = Math.max(1, Math.floor(audio.length / bars));
    const stride = Math.max(1, Math.floor(size / 400));
    const levels: number[] = [];
    for (let i = 0; i < bars; i += 1) {
      let loudest = 0;
      for (let c = 0; c < audio.numberOfChannels; c += 1) {
        const data = audio.getChannelData(c);
        let sum = 0;
        let count = 0;
        for (let j = i * size; j < Math.min(data.length, (i + 1) * size); j += stride) {
          sum += data[j] * data[j];
          count += 1;
        }
        loudest = Math.max(loudest, count ? Math.sqrt(sum / count) : 0);
      }
      levels.push(loudest);
    }
    const max = Math.max(...levels);
    if (!(max > 0)) return null;
    return levels.map(level => Math.max(0.12, level / max));
  } catch {
    return null;
  }
};

const usePlayerStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    player: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '10px 14px 10px 10px',
      borderRadius: 14,
      background: t.surface,
      border: `1px solid ${t.line}`,
      minWidth: 0,
    },
    play: {
      flex: '0 0 auto',
      width: 44,
      height: 44,
      borderRadius: '50%',
      border: 'none',
      display: 'grid',
      placeItems: 'center',
      background: t.text,
      color: t.surface,
      cursor: 'pointer',
      transition: 'transform 120ms ease-out, background-color 150ms ease-out',
      '& svg': { fontSize: 26 },
      '&:hover': { background: t.brand, color: t.brandInk },
      '&:active': { transform: 'scale(0.96)' },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '@media (prefers-reduced-motion: reduce)': { '&:active': { transform: 'none' } },
    },
    scrub: {
      position: 'relative',
      flex: '1 1 auto',
      minWidth: 0,
      height: 36,
      borderRadius: 8,
      '&:focus-within': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    bars: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      overflow: 'hidden',
    },
    bar: {
      flex: '1 1 0',
      minWidth: 1,
      borderRadius: 2,
      background: t.lineStrong,
    },
    barPlayed: {
      background: t.text,
    },
    track: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: '50%',
      height: 4,
      marginTop: -2,
      borderRadius: 2,
      background: t.lineStrong,
      overflow: 'hidden',
    },
    trackFill: {
      height: '100%',
      width: '100%',
      background: t.text,
      transformOrigin: 'left center',
    },
    range: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      margin: 0,
      opacity: 0,
      cursor: 'pointer',
    },
    time: {
      ...CS_FONT.caption,
      flex: '0 0 auto',
      fontWeight: CS_WEIGHT.strong,
      color: t.text2,
      fontVariantNumeric: 'tabular-nums',
    },
    status: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minHeight: 44,
      padding: '10px 14px',
      borderRadius: 14,
      background: t.surface,
      border: `1px solid ${t.line}`,
      ...CS_FONT.small,
      color: t.text2,
    },
    retry: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 36,
      marginLeft: 'auto',
      padding: '0 12px',
      borderRadius: 10,
      border: `1px solid ${t.controlLine}`,
      background: t.surface,
      color: t.text,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      '& svg': { fontSize: 16 },
      '&:hover': { background: t.surface3 },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&:disabled': { cursor: 'progress', color: t.text2 },
    },
  };
});

/**
 * Lazy audio player for a CallScribe recording. Fetches the audio bytes from
 * the API as a blob (so axios attaches the tech's bearer token), wraps the
 * blob in an object URL and feeds it to a native &lt;audio&gt; element.
 *
 * We deliberately do NOT set <audio src> to the raw Twilio media URL — Twilio
 * recording URLs require HTTP Basic auth (AccountSid + AuthToken) and would
 * trigger the browser's OS-level sign-in dialog.
 */
const CallRecordingPlayer: React.FC<ICallRecordingPlayerProps> = ({
  callId,
  seekMs,
  durationSeconds,
}) => {
  const classes = usePlayerStyles();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState<number>(durationSeconds ?? 0);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lastUrlRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadSeq = useRef(0);
  const durationRef = useRef(durationSeconds);
  durationRef.current = durationSeconds;

  const releaseUrl = (): void => {
    if (lastUrlRef.current) {
      URL.revokeObjectURL(lastUrlRef.current);
      lastUrlRef.current = null;
    }
  };

  const onLoad = async (): Promise<void> => {
    loadSeq.current += 1;
    const mine = loadSeq.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const blob = await getCallScribeRecording(callId);
      if (mine !== loadSeq.current) return;
      const url = URL.createObjectURL(blob);
      releaseUrl();
      lastUrlRef.current = url;
      setObjectUrl(url);
      void computePeaks(blob, BARS, durationRef.current).then(next => {
        if (mine === loadSeq.current) setPeaks(next);
      });
    } catch (e) {
      if (mine !== loadSeq.current) return;
      // 404 = not yet uploaded to blob, anything else surfaces as a generic
      // failure. The tech can retry by clicking again.
      const status = (e as { response?: { status?: number } })?.response?.status;
      setErrorMessage(
        status === 404
          ? 'Recording is not available yet.'
          : 'Could not load recording. Try again.',
      );
    } finally {
      if (mine === loadSeq.current) setLoading(false);
    }
  };

  // Auto-fetch the recording on mount so the tech does not have to click
  // "Load recording" first. The blob is small (a few hundred KB for a typical
  // few-minute call) so the up-front cost is acceptable; if a recording is
  // not yet uploaded we surface the 404 as an inline message and the tech
  // can retry via the button.
  // We intentionally depend only on `callId` — `onLoad` is a stable closure scoped
  // to the component instance and capturing it would cause double-fetches on every
  // render. Disabling exhaustive-deps for this single line.
  // eslint-disable-next-line
  useEffect(() => {
    void onLoad();
    return () => {
      loadSeq.current += 1;
      releaseUrl();
      setObjectUrl(null);
      setPeaks(null);
      setPlaying(false);
      setPosition(0);
      setLength(durationRef.current ?? 0);
    };
  }, [callId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || typeof seekMs !== 'number') return;
    audio.currentTime = Math.max(0, seekMs) / 1000;
    audio.play().catch(() => undefined);
  }, [seekMs, objectUrl]);

  const audio = (): HTMLAudioElement | null => audioRef.current;
  const total = length > 0 ? length : 0;
  const played = total > 0 ? Math.min(1, position / total) : 0;

  if (objectUrl) {
    return (
      <div className={classes.player}>
        <button
          type="button"
          className={classes.play}
          aria-label={playing ? 'Pause recording' : 'Play recording'}
          onClick={() => {
            const el = audio();
            if (!el) return;
            if (playing) {
              el.pause();
            } else {
              el.play().catch(() => undefined);
            }
          }}
        >
          {playing ? <PauseRoundedIcon aria-hidden /> : <PlayArrowRoundedIcon aria-hidden />}
        </button>
        <div className={classes.scrub}>
          {peaks ? (
            <div className={classes.bars} aria-hidden="true">
              {peaks.map((level, i) => (
                <span
                  key={i}
                  className={`${classes.bar} ${(i + 0.5) / peaks.length <= played ? classes.barPlayed : ''}`}
                  style={{ height: `${Math.round(level * 100)}%` }}
                />
              ))}
            </div>
          ) : (
            <div className={classes.track} aria-hidden="true">
              <div className={classes.trackFill} style={{ transform: `scaleX(${played})` }} />
            </div>
          )}
          <input
            type="range"
            className={classes.range}
            min={0}
            max={total}
            step={0.1}
            value={Math.min(position, total)}
            aria-label="Position in recording"
            aria-valuetext={`${formatClock(position * 1000)} of ${formatClock(total * 1000)}`}
            onChange={e => {
              const next = Number(e.target.value);
              const el = audio();
              if (el) el.currentTime = next;
              setPosition(next);
            }}
          />
        </div>
        <span className={classes.time}>
          {`${formatClock(position * 1000)} / ${formatClock(total * 1000)}`}
        </span>
        <audio
          ref={audioRef}
          src={objectUrl}
          preload="metadata"
          aria-label="Call recording playback"
          onPlay={() => { setPlaying(true); }}
          onPause={() => { setPlaying(false); }}
          onEnded={() => { setPlaying(false); }}
          onTimeUpdate={e => { setPosition(e.currentTarget.currentTime); }}
          onLoadedMetadata={e => {
            if (Number.isFinite(e.currentTarget.duration) && e.currentTarget.duration > 0) {
              setLength(e.currentTarget.duration);
            }
          }}
          onDurationChange={e => {
            if (Number.isFinite(e.currentTarget.duration) && e.currentTarget.duration > 0) {
              setLength(e.currentTarget.duration);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className={classes.status} role="status">
      <span>{loading ? 'Loading recording…' : errorMessage ?? 'The recording has not loaded.'}</span>
      {loading ? null : (
        <button
          type="button"
          className={classes.retry}
          onClick={() => {
            void onLoad();
          }}
        >
          <RefreshRoundedIcon aria-hidden />
          {errorMessage ? 'Try again' : 'Load recording'}
        </button>
      )}
    </div>
  );
};

export default CallRecordingPlayer;
